import { Grid2 as Grid, Link, Stack, Typography } from '@mui/material';
import {
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EditableListDataGridLayout,
  EditableListFormShowTemplate,
} from 'framework/list';
import { CLIENT_PORTAL_USER_STATUS_LABELS } from 'domain/shared/clientPortal';
import { FormattedTextField, TextField } from 'framework/ui/field';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';
import ClientUserLoginDateField from './ClientUserLoginDateField';

const renderInviteAction = (canInviteClients: boolean, onInviteClick: () => void) => {
  if (!canInviteClients) {
    return null;
  }
  return (
    <Stack spacing={1} direction="row" alignItems="center">
      <Typography color="textSecondary" py={2}>
        To add clients, please
      </Typography>
      <Link component={'button'} onClick={onInviteClick}>
        submit an invitation request
      </Link>
    </Stack>
  );
};

const ClientUsersListContent = ({
  openInviteDrawer,
  hasClientUsers,
}: {
  openInviteDrawer: () => void;
  hasClientUsers: boolean;
}) => {
  const { isAccessGranted } = useProjectSecurityContext();

  const canInviteClients =
    !isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_PORTAL, ACCESS_INFO_ACTIONS.REVIEW) &&
    isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_PORTAL, ACCESS_INFO_ACTIONS.MANAGE);

  if (!hasClientUsers) {
    return (
      <Stack display={'flex'} justifyContent={'flex-end'} alignItems={'center'} spacing={1} py={2}>
        <Typography color="textSecondary">No client users have been invited to the client portal yet.</Typography>
        {renderInviteAction(canInviteClients, openInviteDrawer)}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      <EditableListFormShowTemplate listSource="clientPortalUsers">
        <EditableListDataGridLayout
          disabledAddNewRow
          getRowEnabled={() => true}
          rowProps={() => ({
            sx: {
              alignItems: 'center',
              px: 0,
            },
          })}
          getTableCells={async () => [
            {
              type: EDITABLE_LIST_GROUP,
              component: Grid,
              componentProps: {
                container: true,
                alignItems: 'center',
                justifyContent: 'space-between',
              },
              gridCellProps: {
                size: 12,
              },
              children: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'name',
                  isEditor: false,
                  component: TextField,
                  componentProps: { label: '' },
                  gridCellProps: { size: 3.2 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'email',
                  isEditor: false,
                  component: TextField,
                  componentProps: { label: '' },
                  gridCellProps: { size: 3.1 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'status',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (status: string) => CLIENT_PORTAL_USER_STATUS_LABELS[status] || status,
                  },
                  gridCellProps: { size: 2.7 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'lastLoginAt',
                  isEditor: false,
                  component: ClientUserLoginDateField,
                  componentProps: { label: '' },
                  gridCellProps: { size: 3, display: 'flex', justifyContent: 'flex-end' },
                },
              ],
            },
          ]}
        />
      </EditableListFormShowTemplate>

      {renderInviteAction(canInviteClients, openInviteDrawer)}
    </Stack>
  );
};

export default ClientUsersListContent;
